import React, { useState } from 'react';
import { FaEye, FaEyeSlash, FaSave } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import U2000Label from '../../u2000Core/components/formControls/label/U2000Label';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import { NS } from '../U2106';
import { useU2106ChangePasswordHandlers } from '../hooks/useU2106ChangePasswordHandlers';

const emptyForm = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const inputClass =
  'w-full h-[46px] px-4 pr-12 text-sm bg-surface border border-gray-300 rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2 focus:border-blue-500 focus:ring-blue-500/20 hover:border-gray-400 text-gray-900 disabled:bg-muted disabled:text-gray-500 disabled:cursor-not-allowed';

type PasswordFieldProps = {
  id: string;
  label: string;
  value: string;
  autoComplete: string;
  disabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  showPassword: boolean;
  onToggleVisibility: () => void;
  showLabel: string;
  hideLabel: string;
};

const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  label,
  value,
  autoComplete,
  disabled,
  onChange,
  onFocus,
  onBlur,
  showPassword,
  onToggleVisibility,
  showLabel,
  hideLabel,
}) => (
  <fieldset>
    <U2000Label label={label} />
    <div className="relative">
      <input
        type={showPassword ? 'text' : 'password'}
        id={id}
        name={id}
        autoComplete={autoComplete}
        className={inputClass}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
      />
      <button
        type="button"
        aria-pressed={showPassword}
        aria-label={showPassword ? hideLabel : showLabel}
        className="absolute top-1/2 right-3 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-sm text-gray-400 transition-colors duration-200 hover:text-gray-600 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
        onClick={onToggleVisibility}
        tabIndex={-1}
      >
        {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
      </button>
    </div>
  </fieldset>
);

type Criterion = { label: string; ok: boolean };

const PasswordCriteriaList: React.FC<{ criteria: Criterion[]; title: string }> = ({ criteria, title }) => (
  <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5" aria-live="polite">
    <p className="mb-2 text-xs font-semibold text-gray-700">{title}</p>
    <ul className="space-y-1.5">
      {criteria.map((c) => (
        <li
          key={c.label}
          className={`flex items-center text-xs leading-snug ${c.ok ? 'font-medium text-green-700' : 'text-gray-600'}`}
        >
          <span
            className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${c.ok ? 'bg-green-100' : 'bg-gray-200'}`}
            aria-hidden
          >
            {c.ok ? (
              <svg className="h-2.5 w-2.5 text-green-600" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <span className="h-1 w-1 rounded-full bg-gray-400" />
            )}
          </span>
          {c.label}
        </li>
      ))}
    </ul>
  </div>
);

/** Formulaire mot de passe — fichier séparé pour éviter l’inlining React Compiler dans la garde SSO. */
const U2106ChangePasswordFields: React.FC = () => {
  const { t } = useTranslation(NS);
  const { t: tCommon } = useTranslation('common');
  const { loading, submitChangePassword } = useU2106ChangePasswordHandlers();
  const [form, setForm] = useState(emptyForm);
  const [clientError, setClientError] = useState('');
  const [showNewCriteria, setShowNewCriteria] = useState(false);
  const [visibility, setVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const criteria: Criterion[] = [
    { label: tCommon('msgPasswordMin8Chars'), ok: form.newPassword.length >= 8 },
    { label: tCommon('msgPasswordUppercase'), ok: /[A-Z]/.test(form.newPassword) },
    { label: tCommon('msgPasswordDigit'), ok: /[0-9]/.test(form.newPassword) },
    { label: tCommon('msgPasswordSymbol'), ok: /[!@#$%&*_?|]/.test(form.newPassword) },
  ];

  const allCriteriaMet = criteria.every((c) => c.ok);
  const confirmMismatch =
    form.confirmPassword.length > 0 && form.newPassword !== form.confirmPassword;

  const handleChange = (field: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (clientError) {
      setClientError('');
    }
  };

  const toggleVisibility = (field: keyof typeof visibility) => {
    setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async () => {
    if (loading) {
      return;
    }
    if (!form.oldPassword || !form.newPassword || !form.confirmPassword) {
      setClientError(t('changePassword.errors.required', { ns: NS }));
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setClientError(t('changePassword.errors.confirmationMismatch', { ns: NS }));
      return;
    }
    if (!allCriteriaMet) {
      setClientError(t('changePassword.errors.validation', { ns: NS }));
      setShowNewCriteria(true);
      return;
    }
    const ok = await submitChangePassword(form);
    if (ok) {
      setForm(emptyForm);
      setClientError('');
      setShowNewCriteria(false);
      setVisibility({ oldPassword: false, newPassword: false, confirmPassword: false });
    }
  };

  const showLabel = t('changePassword.showPassword', { ns: NS });
  const hideLabel = t('changePassword.hidePassword', { ns: NS });

  return (
    <div className="flex w-full justify-center px-4 py-6">
      <div className="w-full max-w-md">
        <U2000Panel className="w-full">
          {clientError ? (
            <p className="mb-3 text-sm font-medium text-red-600" role="alert">
              {clientError}
            </p>
          ) : null}

          <form
            className="flex w-full flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
            noValidate
          >
            <PasswordField
              id="oldPassword"
              label={t('changePassword.oldPassword', { ns: NS })}
              value={form.oldPassword}
              autoComplete="current-password"
              disabled={loading}
              onChange={handleChange('oldPassword')}
              showPassword={visibility.oldPassword}
              onToggleVisibility={() => toggleVisibility('oldPassword')}
              showLabel={showLabel}
              hideLabel={hideLabel}
            />

            <div>
              <PasswordField
                id="newPassword"
                label={t('changePassword.newPassword', { ns: NS })}
                value={form.newPassword}
                autoComplete="new-password"
                disabled={loading}
                onChange={handleChange('newPassword')}
                onFocus={() => setShowNewCriteria(true)}
                showPassword={visibility.newPassword}
                onToggleVisibility={() => toggleVisibility('newPassword')}
                showLabel={showLabel}
                hideLabel={hideLabel}
              />
              {showNewCriteria || form.newPassword.length > 0 ? (
                <PasswordCriteriaList criteria={criteria} title={tCommon('lblPasswordCriteria')} />
              ) : null}
            </div>

            <div>
              <PasswordField
                id="confirmPassword"
                label={t('changePassword.confirmPassword', { ns: NS })}
                value={form.confirmPassword}
                autoComplete="new-password"
                disabled={loading}
                onChange={handleChange('confirmPassword')}
                showPassword={visibility.confirmPassword}
                onToggleVisibility={() => toggleVisibility('confirmPassword')}
                showLabel={showLabel}
                hideLabel={hideLabel}
              />
              {confirmMismatch ? (
                <p className="mt-1.5 text-xs font-medium text-red-600" role="status">
                  {t('changePassword.errors.confirmationMismatch', { ns: NS })}
                </p>
              ) : null}
            </div>

            <div className="mt-2 flex justify-end border-t border-gray-100 pt-4">
              <U2000ActionBtn
                label={loading ? t('changePassword.submitting', { ns: NS }) : t('changePassword.submit', { ns: NS })}
                icon={FaSave}
                handleClick={() => {
                  void handleSubmit();
                }}
                disabled={loading}
              />
            </div>
          </form>
        </U2000Panel>
      </div>
    </div>
  );
};

export default U2106ChangePasswordFields;
